import React, { useState, useEffect } from 'react';
import { useDeletedPatients } from 'hooks/trash/useDeletedPatients';
import { useDeletedAiReviews } from 'hooks/trash/useDeletedAiReviews';
import { useRestorePatient } from 'hooks/trash/useRestorePatient';
import { useRestoreAiReview } from 'hooks/trash/useRestoreAiReview';
import TrashTemplate from 'components/templates/TrashTemplate';
import { eventLog } from 'utils/AmplitudeUtil';
import { LogLevel, log } from 'utils/LogUtil';

type TabType = 'patients' | 'aiReviews';

const TrashPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('patients');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // 삭제된 환자 데이터 조회
  const {
    data: patients,
    isLoading: isPatientsLoading,
    isError: isPatientsError,
  } = useDeletedPatients({ page: currentPage, limit: pageSize });

  // 삭제된 AI 리뷰 데이터 조회
  const {
    data: aiReviews,
    isLoading: isAiReviewsLoading,
    isError: isAiReviewsError,
  } = useDeletedAiReviews({ page: currentPage, limit: pageSize });

  // 복원 mutation hooks
  const { mutate: restorePatient, isLoading: isRestoringPatient } = useRestorePatient();
  const { mutate: restoreAiReview, isLoading: isRestoringAiReview } = useRestoreAiReview();

  // 컴포넌트 마운트 시 로깅
  useEffect(() => {
    eventLog('휴지통_페이지_진입', {});
    log(LogLevel.VERBOSE, 'TrashPage: mounted');
  }, []);

  // 탭 변경 시 페이지 초기화
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setCurrentPage(1);
    eventLog('휴지통_탭_변경', { tab });
  };

  // 페이지 변경 처리
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    eventLog('휴지통_페이지_변경', { tab: activeTab, page });
  };

  // 환자 복원 처리
  const handleRestorePatient = (patientId: number) => {
    eventLog('환자_복원_시도', { patientId });
    restorePatient(patientId);
  };

  // AI 리뷰 복원 처리
  const handleRestoreAiReview = (reviewId: number) => {
    eventLog('AI리뷰_복원_시도', { reviewId });
    restoreAiReview(reviewId);
  };

  // Template에 전달할 props 준비
  const templateProps = {
    activeTab,
    onTabChange: handleTabChange,
    patients: patients || [],
    aiReviews: aiReviews || [],
    isLoading: activeTab === 'patients' ? isPatientsLoading : isAiReviewsLoading,
    isError: activeTab === 'patients' ? isPatientsError : isAiReviewsError,
    currentPage,
    pageSize,
    onPageChange: handlePageChange,
    onRestorePatient: handleRestorePatient,
    onRestoreAiReview: handleRestoreAiReview,
    isRestoring: isRestoringPatient || isRestoringAiReview,
  };

  return <TrashTemplate {...templateProps} />;
};

export default TrashPage;